// Server-Sent Events (SSE) wire-format parser, stream-friendly. The upstream
// FastAPI service emits frames shaped:
//
//   data: {"text":"chunk"}\n\n
//   data: {"error":"..."}\n\n
//   data: [DONE]\n\n
//
// We can't assume a chunk boundary lands on a frame boundary, so the parser
// keeps a residual buffer and only emits whole frames terminated by a blank
// line (\n\n or \r\n\r\n). Callers feed bytes via `push(chunk)` and pull
// completed events from the returned array; `flush()` returns whatever
// trailing data lacks a terminator (currently unused — the stream's [DONE]
// frame is always terminated — but kept for graceful close handling).

export type SseEvent = { data: string };

export class SseParser {
  private buffer = '';

  push(chunk: string): SseEvent[] {
    this.buffer += chunk;
    const events: SseEvent[] = [];
    // Normalize CRLF to LF so the frame splitter below has one shape to match.
    this.buffer = this.buffer.replace(/\r\n/g, '\n');

    let blankIdx = this.buffer.indexOf('\n\n');
    while (blankIdx !== -1) {
      const frame = this.buffer.slice(0, blankIdx);
      this.buffer = this.buffer.slice(blankIdx + 2);

      const data = this.parseFrame(frame);
      if (data !== null) events.push({ data });

      blankIdx = this.buffer.indexOf('\n\n');
    }
    return events;
  }

  flush(): SseEvent[] {
    if (!this.buffer) return [];
    const data = this.parseFrame(this.buffer);
    this.buffer = '';
    return data !== null ? [{ data }] : [];
  }

  // One SSE frame is 1+ lines; `data:` fields are joined with '\n' per the spec.
  // Non-`data:` lines (`:` comments, custom event names) are ignored.
  private parseFrame(frame: string): string | null {
    const lines = frame.split('\n');
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('data:')) {
        // The spec strips a single leading space if present.
        const value = line.slice(5);
        dataLines.push(value.startsWith(' ') ? value.slice(1) : value);
      }
    }
    return dataLines.length ? dataLines.join('\n') : null;
  }
}
